import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  withCredentials: true
});

export function AnalysisResult() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await api.get(`/api/analysis/${analysisId}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Analysis not found');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [analysisId]);

  const splitIntoSections = (markdown) => {
    if (!markdown) return [];
    
    // Split by markdown headers instead of HTML tags
    const sections = markdown.split(/(?=### )/);
    return sections.filter(section => section.trim());
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <p className="text-xl text-zinc-400">Loading analysis...</p>
    </div>
  );
  
  if (error) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <p className="text-xl text-red-500">{error}</p>
    </div>
  );
  
  if (!data) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <p className="text-xl text-zinc-400">No data found</p>
    </div>
  );

  return (
    <div className="grid md:grid-cols-[300px,1fr] gap-8">
      <div className="space-y-6">
        <div className="screenshot-container">
          <div className="screenshot-preview">
            <img 
              src={data.screenshotUrl} 
              alt="Website screenshot" 
              className="screenshot-image"
              onClick={() => setIsModalOpen(true)}
            />
            <button 
              className="expand-button"
              onClick={() => setIsModalOpen(true)}
            >
              Click to expand
            </button>
          </div>
        </div>

        <Button 
          onClick={() => navigate('/')}
          size="lg"
          className="w-full h-14 px-8 bg-white hover:bg-gradient-to-r from-orange-500 to-pink-500 hover:animate-gradient-x text-black text-lg font-medium transition-all duration-300 group"
        >
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          Scan Another Page
        </Button>
      </div>

      <div className="space-y-6">
      {splitIntoSections(data.analysis).map((section, index) => (
          <Card key={index} className="bg-[#1A1A1A] border-[#333] hover:bg-[#222] transition-all duration-300 text-zinc-400">
            <CardContent className="p-6">
              <div className="prose prose-invert max-w-none">
                <ReactMarkdown
                    components={{
                      h3: ({node, children, ...props}) => (
                        <h3 className="text-xl font-semibold mb-2 text-white" {...props}>
                          {children}
                        </h3>
                      )
                    }}
                  >
                  {section}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button 
              className="close-modal"
              onClick={() => setIsModalOpen(false)}
            >
              ×
            </button>
            <img 
              src={data.screenshotUrl} 
              alt="Full website screenshot" 
              className="modal-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}